import React from "react";
import Container from "@/components/marketing/Container";
import SectionHeading from "@/components/marketing/SectionHeading";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useOutletRecords } from "@/hooks/useOutletRecords";
import PlacementCard from "@/components/marketing/PlacementCard";

// Wall config: each entry maps to a product record by name. Entrepreneur Middle
// East is stored on the "Entrepreneur ME (Staff)" record and displayed as
// "Entrepreneur Middle East". All other fields (logo, DA, link type, price,
// Tier 1) are read live from the record at runtime.
const PLACEMENTS = [
  { name: "Khaleej Times", slug: "khaleej-times" },
  { name: "Gulf News", slug: "gulf-news" },
  { name: "Arabian Business", slug: "arabian-business" },
  { name: "Entrepreneur ME (Staff)", display: "Entrepreneur Middle East", slug: "entrepreneur-me-staff" },
  { name: "MENA FN", slug: "mena-fn" },
  { name: "Eye of Riyadh", slug: "eye-of-riyadh" },
];

const BUDGET = [
  { name: "Dubai Week", slug: "dubai-week" },
  { name: "Dubai Weekly", slug: "dubai-weekly" },
  { name: "Dubai Business Journal", slug: "dubai-business-journal" },
];

const GLOBAL = ["Financial Times", "Reuters", "Yahoo Finance", "MarketWatch", "Business Insider"];

export default function UaePlacements() {
  const records = useOutletRecords([
    ...PLACEMENTS.map((p) => p.name),
    ...BUDGET.map((b) => b.name),
  ]);

  return (
    <section className="py-12 lg:py-16 bg-canvas">
      <Container>
        <SectionHeading
          tag="GUARANTEED PLACEMENTS"
          title="Named UAE outlets. Guaranteed publication."
          subtitle="Choose the exact publication. We guarantee it publishes, or you get a full refund. Here are some of the outlets available for UAE campaigns."
        />

        <p className="text-center text-sm text-steel -mt-8 mb-10">Fixed prices from $100 to $2,655, shown before you order.</p>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 lg:gap-5">
          {PLACEMENTS.map((p) => (
            <PlacementCard
              key={p.name}
              record={records[p.name]}
              name={p.name}
              display={p.display}
              href={`/outlets/${p.slug}`}
            />
          ))}
        </div>

        {/* Budget friendly UAE titles (live prices from records) */}
        <div className="mt-8 text-center">
          <p className="text-sm text-steel mb-3">Budget friendly UAE titles:</p>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
            {BUDGET.map((b) => {
              const rec = records[b.name];
              const price = rec ? (rec.discount_price_usd != null ? rec.discount_price_usd : rec.price_usd) : null;
              return (
                <Link
                  key={b.slug}
                  to={`/outlets/${b.slug}`}
                  className="text-sm font-medium text-faded hover:text-bone transition-colors"
                >
                  {b.name}{price != null ? ` $${Math.round(price).toLocaleString("en-US")}` : ""}
                </Link>
              );
            })}
          </div>
        </div>

        {/* Also-available text strip */}
        <div className="mt-6 text-center">
          <p className="text-sm text-steel mb-3">Plus global reach when you need it:</p>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
            {GLOBAL.map((name) => (
              <span key={name} className="text-sm font-medium text-faded hover:text-bone transition-colors">
                {name}
              </span>
            ))}
          </div>
        </div>

        <div className="mt-10 text-center">
          <Link
            to="/services/guaranteed-placements"
            className="inline-flex items-center text-sm font-medium text-cobalt hover:text-cobalt/80 transition-colors"
          >
            Browse all guaranteed placements <ArrowRight className="ml-1.5 w-4 h-4" />
          </Link>
        </div>
      </Container>
    </section>
  );
}